/** @jsxImportSource @emotion/react */
import { useEffect, useRef } from 'react'
import { createEmbeddingContext, EmbeddingContext } from 'amazon-quicksight-embedding-sdk'

import { useLazyGetQuickSightEmbedUrlQuery } from 'store/services/managementReports'

const CONTAINER_ID = 'quicksight'

type QuickSightEmbedProps = {
  dashboardId: string
}

/**
 * Embeds a single QuickSight dashboard via the anonymous-embed flow (the backend tags the
 * embed session with the requesting user's office/company). This is the one place that talks
 * to the QuickSight embedding SDK — every dashboard screen (Analytics, BillingAnalytics, ...)
 * renders this component instead of re-implementing the iframe lifecycle.
 */
export const QuickSightEmbed = ({ dashboardId }: QuickSightEmbedProps) => {
  const ecRef = useRef<EmbeddingContext>()
  const [getEmbedUrl] = useLazyGetQuickSightEmbedUrlQuery()

  useEffect(() => {
    const embed = async (id: string) => {
      document.querySelectorAll('iframe').forEach((iframe) => iframe.remove())
      const { url } = await getEmbedUrl({ dashboardId: id, userType: 'LAB' }).unwrap()

      if (!ecRef.current) {
        ecRef.current = await createEmbeddingContext()
      }
      const container = document.getElementById(CONTAINER_ID)
      if (!container) return
      ecRef.current.embedDashboard({
        url,
        container,
        height: '100%',
        width: '100%',
      })
    }

    embed(dashboardId)
  }, [dashboardId, getEmbedUrl])

  return <div id={CONTAINER_ID} css={{ height: 'calc(100vh - 50px)' }} />
}

export default QuickSightEmbed
